import IdListDTO from 'models/dto/IdListDTO';
import ResourceLinkAudienceDTO from 'models/dto/resourceLinkAudienceDTO';
import ResourceLinkChannelDTO from 'models/dto/resourceLinkChannelDTO';
import ResourceLinkDTO, {
  EntityLinkDTO,
  PublicationResultDTO,
  ResourceLinkVersionDTO,
} from 'models/dto/resourceLinkDTO';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import ResourceLink, { EntityLink, PublicationResult, ResourceLinkVersion } from 'models/resourceLink';
import ResourceLinkAudience from 'models/resourceLinkAudience';
import ResourceLinkChannel from 'models/resourceLinkChannel';
import ResourceList from 'models/resourceList';
import AppError from 'utils/appError';
import { fromApiDateTimeOptional, toApiDateTimeOptional } from 'utils/datetime';
import { mapFromEntity } from './activityMapping';
import { mapFromApprovals } from './approvalMapping';
import { mapFromTask } from './taskMapping';
import { mapFromUserReadConfirmations } from './userMapping';

export function mapFromResourceLink(link: ResourceLinkDTO, cache: GlobalDataCache): ResourceLink {
  const output = new ResourceLink();
  output.linkId = link.linkId;
  output.linkName = link.linkName;
  output.linkURL = link.linkURL;
  output.linkURLFragment = link.linkURLFragment;
  output.usedCount = link.usedCount;
  output.entity = link.entity ? mapFromEntity(link.entity) : undefined;
  output.listId = link.listId;
  output.listItemId = link.listItemId;
  output.driveItemId = link.driveItemId;
  output.pageId = link.pageId;
  output.externalId = link.externalId;
  output.pinned = link.pinned;
  output.readRequest = link.readRequest;
  output.linkTypeId = link.linkTypeId;
  output.list = cache.lists.get(link.listId);
  output.resourceLinkChannels = mapFromResourceLinkChannels(link.resourceLinkChannels);
  output.approvals = mapFromApprovals(link.approvals, cache);
  output.ownerId = link.ownerId;
  output.ownerRoleId = link.ownerRoleId;
  output.normIds = link.normIds?.idList?.map((n) => Number.parseInt(n));
  output.versionInfo = mapFromResourceLinkVersion(link.versionInfo);
  output.userReadConfirmations = mapFromUserReadConfirmations(link.userReadConfirmations, cache);
  output.publicationId = link.publicationId;
  output.lastPublishedVersion = link.lastPublishedVersion;
  output.sourceDriveId = link.sourceDriveId;

  return output;
}

export function mapFromResourceLinkWithLists(link: ResourceLinkDTO, lists: ResourceList[]): ResourceLink {
  const output = new ResourceLink();
  output.linkId = link.linkId;
  output.linkName = link.linkName;
  output.linkURL = link.linkURL;
  output.linkURLFragment = link.linkURLFragment;
  output.usedCount = link.usedCount;
  output.entity = link.entity ? mapFromEntity(link.entity) : undefined;
  output.listId = link.listId;
  output.listItemId = link.listItemId;
  output.driveItemId = link.driveItemId;
  output.pageId = link.pageId;
  output.externalId = link.externalId;
  output.pinned = link.pinned;
  output.readRequest = link.readRequest;
  output.resourceLinkChannels = mapFromResourceLinkChannels(link.resourceLinkChannels);
  output.ownerId = link.ownerId;
  output.ownerRoleId = link.ownerRoleId;
  output.normIds = link.normIds?.idList?.map((n) => Number.parseInt(n));
  output.sourceDriveId = link.sourceDriveId;

  const list = lists.find((l) => l.listId === link.listId);
  if (list) {
    output.list = list;
  } else {
    throw new AppError('List not found: ' + link.listId.toString());
  }

  return output;
}

export function mapFromResourceLinks(links: ResourceLinkDTO[] | undefined, cache: GlobalDataCache): ResourceLink[] {
  if (!links) return [];

  return links.map((_link) => {
    return mapFromResourceLink(_link, cache);
  });
}

export function mapFromResourceLinksWithLists(
  links: ResourceLinkDTO[] | undefined,
  lists: ResourceList[],
): ResourceLink[] {
  if (!links) return [];

  return links.map((_link) => {
    return mapFromResourceLinkWithLists(_link, lists);
  });
}

export function mapToResourceLinks(
  links: ResourceLink[] | undefined,
  includeMetadata: boolean,
): ResourceLinkDTO[] | undefined {
  if (!links) return undefined;

  return links.map((_link) => {
    return mapToResourceLink(_link, includeMetadata);
  });
}

export function mapToResourceLink(link: ResourceLink, includeMetadata: boolean): ResourceLinkDTO {
  const output = new ResourceLinkDTO();
  output.linkId = link.linkId;
  output.linkName = link.linkName;
  output.linkURL = link.linkURL;
  output.linkURLFragment = link.linkURLFragment;
  output.listId = link.listId;
  output.listItemId = link.listItemId;
  output.driveItemId = link.driveItemId;
  output.pageId = link.pageId;
  output.externalId = link.externalId;
  output.pinned = link.pinned;
  output.readRequest = link.readRequest;
  output.ownerId = link.ownerId;
  output.ownerRoleId = link.ownerRoleId;
  output.versions = includeMetadata ? mapToResourceLinkVersions(link.versions) : [];
  output.normIds = link.normIds ? new IdListDTO(link.normIds.map((id) => id.toString())) : undefined;
  output.sourceDriveId = link.sourceDriveId;

  return output;
}

//
// Resource Link Versions
//
export function mapToResourceLinkVersions(versions: ResourceLinkVersion[]): ResourceLinkVersionDTO[] {
  return versions.map((version) => {
    return mapToResourceLinkVersion(version);
  });
}

export function mapToResourceLinkVersion(input: ResourceLinkVersion): ResourceLinkVersionDTO {
  const output = new ResourceLinkVersionDTO();
  output.id = input.id;
  output.date = toApiDateTimeOptional(input.date);
  output.version = input.version;

  return output;
}

export function mapFromResourceLinkVersion(version: ResourceLinkVersionDTO | undefined): ResourceLinkVersion {
  const output = new ResourceLinkVersion();
  if (!version) return output;
  output.id = version.id;
  output.date = fromApiDateTimeOptional(version.date);
  output.version = version.version;

  return output;
}

//
// Entity Links
//
export function mapFromEntityLinks(entityDTOs: EntityLinkDTO[] | undefined): EntityLink[] {
  if (!entityDTOs) return [];

  return entityDTOs.map((entity) => {
    return mapFromEntityLink(entity);
  });
}

export function mapFromEntityLink(entityDTO: EntityLinkDTO): EntityLink {
  const entity = new EntityLink();
  entity.entityId = entityDTO.entityId;
  entity.typeOfEntity = entityDTO.typeOfEntity;
  entity.entityName = entityDTO.entityName;
  entity.entityCode = entityDTO.entityCode;
  entity.pinned = entityDTO.pinned;
  entity.linkURLFragment = entityDTO.linkURLFragment;
  entity.data = entityDTO.data;

  return entity;
}

//
// Resource Link Channels
//
export function mapFromResourceLinkChannels(
  resourceLinkChannelDTOs: ResourceLinkChannelDTO[] | undefined,
): ResourceLinkChannel[] | undefined {
  if (!resourceLinkChannelDTOs) return undefined;

  return resourceLinkChannelDTOs.map((r) => mapFromResourceLinkChannel(r));
}

export function mapFromResourceLinkChannel(resourceLinkChannelDTO: ResourceLinkChannelDTO): ResourceLinkChannel {
  const output = new ResourceLinkChannel();
  output.linkId = resourceLinkChannelDTO.linkId;
  output.groupId = resourceLinkChannelDTO.groupId;
  output.channelId = resourceLinkChannelDTO.channelId;
  output.pinned = resourceLinkChannelDTO.pinned;

  return output;
}

//
// Resource Link Audiences
//
export function mapFromResourceLinkAudiences(
  resourceLinkAudienceDTOs: ResourceLinkAudienceDTO[] | undefined,
): ResourceLinkAudience[] | undefined {
  if (!resourceLinkAudienceDTOs) return undefined;

  return resourceLinkAudienceDTOs.map((r) => mapFromResourceLinkAudience(r));
}

export function mapFromResourceLinkAudience(resourceLinkAudienceDTO: ResourceLinkAudienceDTO): ResourceLinkAudience {
  const output = new ResourceLinkAudience();
  output.linkId = resourceLinkAudienceDTO.linkId;
  output.audienceId = resourceLinkAudienceDTO.audienceId;
  output.readRequest = resourceLinkAudienceDTO.readRequest;

  return output;
}

export function mapToResourceLinkAudiences(
  resourceLinkAudiences: ResourceLinkAudience[] | undefined,
): ResourceLinkAudienceDTO[] | undefined {
  if (!resourceLinkAudiences) return undefined;

  return resourceLinkAudiences.map((r) => mapToResourceLinkAudience(r));
}

export function mapToResourceLinkAudience(resourceLinkAudience: ResourceLinkAudience): ResourceLinkAudienceDTO {
  const output = new ResourceLinkAudienceDTO();
  output.linkId = resourceLinkAudience.linkId;
  output.audienceId = resourceLinkAudience.audienceId;
  output.readRequest = resourceLinkAudience.readRequest;
  output.delete = resourceLinkAudience.delete;

  return output;
}

export function mapFromPublicationResults(
  resultsDTO: PublicationResultDTO[] | undefined,
  cache: GlobalDataCache,
): PublicationResult[] {
  if (!resultsDTO) return [];

  return resultsDTO.map((result) => {
    const output = new PublicationResult();
    output.publicationId = result.publicationId;
    output.linkId = result.linkId;
    output.created = fromApiDateTimeOptional(result.created);
    output.createdById = result.createdById;
    output.status = result.status;
    output.result = result.result;
    output.readConfirmationsSend = result.readConfirmationsSend;
    output.smallChange = result.smallChange;
    output.link = result.link ? mapFromResourceLink(result.link, cache) : undefined;
    output.oldVersion = result.oldVersion;
    output.newVersion = result.newVersion;
    output.lastModified = fromApiDateTimeOptional(result.lastModified);
    output.approvalCompleted = fromApiDateTimeOptional(result.approvalCompleted);
    output.approvalState = result.approvalState;
    output.approvers = result.approvers;
    output.approvalTask = result.approvalTask ? mapFromTask(result.approvalTask, cache) : undefined;

    return output;
  });
}
